// SEO information architecture: 11 sections in 4 groups, each with sub-views shown as tabs.
//
// URL scheme (query parameters, so filters such as `keywordStatus` or `backlinkStatus` can ride
// along unchanged):
//   /projects/:projectId/seo?tab=<section>[&view=<sub-view>]
// The sub-view is omitted for a section's first view. Every older `?tab=` / `?tool=` / `?insight=`
// link is translated by `legacySeoLocation` and rewritten in place by the workspace page.

export const SEO_TOOLS = [
  {
    value: "keywords",
    label: "Keyword research",
    description: "Discover searches, compare demand and choose relevant keywords.",
    step: "Discover",
  },
  {
    value: "serp",
    label: "Google rank checks",
    description: "Paid Google checks of approved keywords: positions and the pages that rank.",
    step: "Measure",
  },
  {
    value: "competitors",
    label: "Competitor research",
    description: "Find competing sites, keyword gaps and their strongest pages.",
    step: "Compare",
  },
  {
    value: "audit",
    label: "Site audit",
    description: "Check technical issues, page speed and real-user performance.",
    step: "Improve",
  },
  {
    value: "planning",
    label: "Plan & briefs",
    description: "Prioritize saved research and turn it into content briefs.",
    step: "Create",
  },
  {
    value: "report",
    label: "Report & PDF",
    description: "Rankings, research and delivery in one report, ready to export.",
    step: "Report",
  },
] as const;

export type SeoTool = (typeof SEO_TOOLS)[number]["value"] | "overview" | "settings";

export type SeoSectionId =
  | "dashboard"
  | "rankings"
  | "local"
  | "keywords"
  | "traffic"
  | "research"
  | "site-health"
  | "backlinks"
  | "content"
  | "reports"
  | "settings";

export type SeoGroup = "Performance" | "Research" | "Site & delivery" | "Reporting & setup";

export type SeoView = {
  id: string;
  label: string;
  /** Internal team only: hidden from external client users. */
  internal?: true;
};

export type SeoSection = {
  id: SeoSectionId;
  label: string;
  /** Sidebar group; the dashboard stands on its own above the groups. */
  group: SeoGroup | null;
  /** One line under the page title. */
  purpose: string;
  views: readonly SeoView[];
  internal?: true;
};

export type SeoLocation = { section: SeoSectionId; view: string };

const SECTIONS: readonly SeoSection[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    group: null,
    purpose: "Search performance at a glance, with the next actions to take.",
    views: [{ id: "overview", label: "Overview" }],
  },
  {
    id: "rankings",
    label: "Rankings",
    group: "Performance",
    purpose: "Where tracked keywords appear in Google, and how that changes over time.",
    // "positions" keeps its id so links to the former "Positions & history" still open it.
    views: [
      { id: "overview", label: "Overview" },
      { id: "competitors", label: "Competitors", internal: true },
      { id: "ai-visibility", label: "AI visibility" },
      { id: "brand-mentions", label: "Brand mentions" },
      { id: "positions", label: "History & sources" },
      { id: "checks", label: "Google rank checks", internal: true },
    ],
  },
  {
    id: "local",
    label: "Local",
    group: "Performance",
    purpose:
      "Where the business appears in the map pack, its Google business profile and what reviews say.",
    views: [
      { id: "map-rankings", label: "Map rankings" },
      { id: "business-profile", label: "Business profile" },
      { id: "reviews", label: "Reviews", internal: true },
    ],
  },
  {
    id: "keywords",
    label: "Keywords",
    group: "Performance",
    purpose: "The agreed keyword plan: targets, tracking groups and approvals.",
    views: [
      { id: "plan", label: "Keyword plan" },
      { id: "opportunities", label: "Opportunities", internal: true },
    ],
  },
  {
    id: "traffic",
    label: "Traffic",
    group: "Performance",
    purpose: "Visits from search, engagement and key events.",
    views: [{ id: "analytics", label: "Google Analytics" }],
  },
  {
    id: "research",
    label: "Research",
    group: "Research",
    purpose: "Keyword and competitor opportunities, turned into a plan and briefs.",
    views: [
      { id: "keywords", label: "Keyword research", internal: true },
      { id: "competitors", label: "Competitor research", internal: true },
      { id: "organic", label: "Organic research" },
      { id: "gap", label: "Keyword gap", internal: true },
      { id: "competitor-analysis", label: "Competitor analysis" },
      { id: "plan", label: "Plan & briefs", internal: true },
      { id: "ask", label: "Ask the site", internal: true },
    ],
  },
  {
    id: "site-health",
    label: "Site health",
    group: "Site & delivery",
    purpose: "Technical issues, page speed and pages competing for the same searches.",
    views: [
      { id: "audit", label: "Site audit", internal: true },
      { id: "issues", label: "Technical issues" },
      { id: "indexing", label: "Indexing" },
      { id: "pages", label: "Pages" },
      { id: "performance", label: "Performance" },
      { id: "overlap", label: "Page overlap" },
    ],
  },
  {
    id: "backlinks",
    label: "Backlinks",
    group: "Site & delivery",
    purpose: "Links from other websites, guest posts and PR, with verification.",
    views: [
      { id: "all", label: "All backlinks" },
      { id: "guest-pr", label: "Guest posts & PR" },
      { id: "profile", label: "Link profile" },
      { id: "opportunities", label: "Link opportunities", internal: true },
    ],
  },
  {
    id: "content",
    label: "Content",
    group: "Site & delivery",
    purpose: "Articles from draft to publication, and delivery milestones.",
    views: [
      { id: "articles", label: "Articles" },
      { id: "delivery", label: "Delivery" },
      { id: "writer", label: "AI writer", internal: true },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    group: "Reporting & setup",
    purpose: "Shareable reports and historical measurements, with their sources.",
    views: [
      { id: "report", label: "Report & PDF", internal: true },
      { id: "history", label: "Historical data" },
      { id: "client-reports", label: "Client reports" },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    group: "Reporting & setup",
    purpose: "The website, search market, rank-check schedule and connected services.",
    internal: true,
    views: [
      { id: "website", label: "Website" },
      { id: "rank-checks", label: "Rank checks" },
      { id: "integrations", label: "Integrations" },
      { id: "schedules", label: "Schedules" },
    ],
  },
];

export const SEO_GROUPS: readonly SeoGroup[] = [
  "Performance",
  "Research",
  "Site & delivery",
  "Reporting & setup",
];

const SECTION_IDS = new Set<string>(SECTIONS.map((section) => section.id));

export function isSeoSectionId(value: string | null): value is SeoSectionId {
  return value !== null && SECTION_IDS.has(value);
}

/** The sections (and their sub-views) available to this audience, in menu order. */
export function seoSections(external: boolean): SeoSection[] {
  if (!external) return [...SECTIONS];
  return SECTIONS.filter((section) => !section.internal)
    .map((section) => ({ ...section, views: section.views.filter((view) => !view.internal) }))
    .filter((section) => section.views.length > 0);
}

export function seoSection(id: SeoSectionId, external: boolean): SeoSection {
  const sections = seoSections(external);
  return sections.find((section) => section.id === id) ?? sections[0]!;
}

const TOOL_LOCATIONS: Record<SeoTool, SeoLocation> = {
  overview: { section: "dashboard", view: "overview" },
  serp: { section: "rankings", view: "checks" },
  keywords: { section: "research", view: "keywords" },
  competitors: { section: "research", view: "competitors" },
  planning: { section: "research", view: "plan" },
  audit: { section: "site-health", view: "audit" },
  report: { section: "reports", view: "report" },
  settings: { section: "settings", view: "rank-checks" },
};

function isSeoTool(value: string | null): value is SeoTool {
  return value !== null && Object.hasOwn(TOOL_LOCATIONS, value);
}

const LEGACY_TABS: Record<string, SeoLocation> = {
  overview: { section: "dashboard", view: "overview" },
  rankings: { section: "rankings", view: "overview" },
  keywords: { section: "keywords", view: "plan" },
  analytics: { section: "traffic", view: "analytics" },
  backlinks: { section: "backlinks", view: "all" },
  guest_pr: { section: "backlinks", view: "guest-pr" },
  content: { section: "content", view: "articles" },
  delivery: { section: "content", view: "delivery" },
  analysis: { section: "reports", view: "history" },
  setup: { section: "settings", view: "website" },
};

const LEGACY_INSIGHTS: Record<string, SeoLocation> = {
  technical: { section: "site-health", view: "issues" },
  competitors: { section: "research", view: "competitor-analysis" },
  overlap: { section: "site-health", view: "overlap" },
};

/**
 * Translates a link from the previous SEO layout (17 menu items: `tab` + `tool` + `insight`).
 * Returns null when the URL already uses the current scheme.
 */
export function legacySeoLocation(params: URLSearchParams): SeoLocation | null {
  const tab = params.get("tab");
  const tool = params.get("tool");
  const insight = params.get("insight");
  // Section ids that kept their name (rankings, keywords, backlinks, content) are current.
  if (isSeoSectionId(tab)) return null;
  if (tab === null && tool === null && insight === null) return null;
  if (tab === "tools" || tab === null) {
    if (isSeoTool(tool)) return TOOL_LOCATIONS[tool];
    // Shared links once used the Insights selector to open competitor research.
    if (tool === null && insight === "competitors") return TOOL_LOCATIONS.competitors;
    return TOOL_LOCATIONS.overview;
  }
  if (tab === "insights")
    return LEGACY_INSIGHTS[insight ?? "technical"] ?? LEGACY_INSIGHTS.technical!;
  return LEGACY_TABS[tab] ?? TOOL_LOCATIONS.overview;
}

/**
 * A Google sign-in for Search Console that returns to Rankings (clients connect it there) lands
 * on History & sources, where the connection card and any sign-in error are shown.
 */
function searchConsoleSignInView(params: URLSearchParams) {
  return params.get("tab") === "rankings" &&
    !params.has("view") &&
    (params.has("searchConsole") || (params.has("google") && params.get("service") !== "ga4"))
    ? "positions"
    : null;
}

/** Where these parameters point for this audience, falling back to what they may open. */
export function resolveSeoLocation(params: URLSearchParams, external: boolean): SeoLocation {
  const tab = params.get("tab");
  const requested = legacySeoLocation(params) ?? {
    section: isSeoSectionId(tab) ? tab : "dashboard",
    view: params.get("view") ?? searchConsoleSignInView(params) ?? "",
  };
  const sections = seoSections(external);
  const section = sections.find((item) => item.id === requested.section);
  if (!section) return { section: sections[0]!.id, view: sections[0]!.views[0]!.id };
  const view = section.views.find((item) => item.id === requested.view) ?? section.views[0]!;
  return { section: section.id, view: view.id };
}

/**
 * The search string (without "?") these parameters should be rewritten to, or null when they
 * are already canonical. Unrelated parameters (filters, OAuth outcomes) are kept.
 */
export function seoCanonicalSearch(params: URLSearchParams, external: boolean): string | null {
  const legacy = legacySeoLocation(params);
  const location = resolveSeoLocation(params, external);
  const view = params.get("view");
  const viewIsValid =
    view === null || seoSection(location.section, external).views.some((item) => item.id === view);
  if (!legacy && params.get("tab") === location.section && viewIsValid) return null;
  if (!legacy && params.get("tab") === null && view === null) return null;
  const next = new URLSearchParams({ tab: location.section });
  if (location.view !== defaultViewId(location.section)) next.set("view", location.view);
  for (const [key, value] of params)
    if (!["tab", "tool", "insight", "view"].includes(key)) next.append(key, value);
  return next.toString();
}

function defaultViewId(section: SeoSectionId) {
  return SECTIONS.find((item) => item.id === section)!.views[0]!.id;
}

/** Link to a section (and optionally a sub-view). A null project opens the SEO home first. */
export function seoSectionHref(
  projectId: string | null,
  section: SeoSectionId,
  view?: string,
  extra?: Record<string, string>,
) {
  const params = new URLSearchParams({ tab: section });
  if (view && view !== defaultViewId(section)) params.set("view", view);
  for (const [key, value] of Object.entries(extra ?? {})) params.set(key, value);
  return `${projectId ? `/projects/${encodeURIComponent(projectId)}/seo` : "/seo"}?${params}`;
}

export function seoToolHref(projectId: string, tool: SeoTool = "overview") {
  const location = TOOL_LOCATIONS[tool];
  return seoSectionHref(projectId, location.section, location.view);
}

/** The research/collection tool panel that renders a location, if it is one. */
export function seoEngineTool(location: SeoLocation): SeoTool | null {
  for (const [tool, target] of Object.entries(TOOL_LOCATIONS) as [SeoTool, SeoLocation][])
    if (target.section === location.section && target.view === location.view) return tool;
  return null;
}

export function resolveSeoTool(params: URLSearchParams): SeoTool {
  return seoEngineTool(resolveSeoLocation(params, false)) ?? "overview";
}

export function seoSectionFor(params: URLSearchParams, external: boolean): SeoSection {
  return seoSection(resolveSeoLocation(params, external).section, external);
}

// A project switch preserves the destination, but never carries record IDs,
// import outcomes or filters belonging to the previous project.
export function seoProjectSwitchHref(
  projectId: string,
  params: URLSearchParams,
  external: boolean,
) {
  const location = resolveSeoLocation(params, external);
  return seoSectionHref(projectId, location.section, location.view);
}
